/**
 * Mapping Lucide React → Phosphor Icons avec support automatique du thème
 * 
 * Ce fichier réexporte les icônes Phosphor avec les noms Lucide pour faciliter la migration.
 * Les icônes utilisent automatiquement le style défini dans le contexte de personnalisation.
 * 
 * Usage:
 *   import { Home, Settings, User } from '../lib/icons';
 *   <Home size={24} className="text-primary" />
 */

import React, { useState, useEffect } from 'react';
import {
  Pulse as ActivityPh,
  Robot as RobotPh,
  QrCode as QrCodePh,
  ArrowClockwise as ArrowClockwisePh,
  ArrowCounterClockwise as ArrowCounterClockwisePh,
  ArrowLeft as ArrowLeftPh,
  ArrowRight as ArrowRightPh,
  ArrowUp as ArrowUpPh,
  ArrowDown as ArrowDownPh,
  ArrowSquareOut as ArrowSquareOutPh,
  ArrowsIn as ArrowsInPh,
  ArrowsLeftRight as ArrowsLeftRightPh,
  ArrowsOut as ArrowsOutPh,
  ArrowsOutCardinal as ArrowsOutCardinalPh,
  ArrowsOutSimple as ArrowsOutSimplePh,
  At as AtPh,
  Bell as BellPh,
  BookOpen as BookOpenPh,
  BookmarkSimple as BookmarkSimplePh,
  Briefcase as BriefcasePh,
  Building as BuildingPh,
  Buildings as BuildingsPh,
  Calendar as CalendarPh,
  CalendarBlank as CalendarBlankPh,
  Camera as CameraPh,
  CaretDoubleLeft as CaretDoubleLeftPh,
  CaretDoubleRight as CaretDoubleRightPh,
  CaretDown as CaretDownPh,
  CaretLeft as CaretLeftPh,
  CaretRight as CaretRightPh,
  CaretUp as CaretUpPh,
  ChartBar as ChartBarPh,
  ChatCircle as ChatCirclePh,
  ChatText as ChatSquarePh,
  Check as CheckPh,
  CheckCircle as CheckCirclePh,
  Checks as ChecksPh,
  Circle as CirclePh,
  CircleNotch as CircleNotchPh,
  Clock as ClockPh,
  Cloud as CloudPh,
  HardDrives as HardDrivesPh,
  Compass as CompassPh,
  Copy as CopyPh,
  Crosshair as CrosshairPh,
  DeviceMobile as DeviceMobilePh,
  DotsSixVertical as DotsSixVerticalPh,
  DotsThree as DotsThreePh,
  DotsThreeVertical as DotsThreeVerticalPh,
  Download as DownloadPh,
  Envelope as EnvelopePh,
  Export as ExportPh,
  Eye as EyePh,
  EyeSlash as EyeSlashPh,
  FileArrowDown as FileArrowDownPh,
  FileAudio as FileAudioPh,
  Files as FilesPh,
  FileText as FileTextPh,
  FileXls as FileXlsPh,
  Flame as FlamePh,
  FloppyDisk as FloppyDiskPh,
  Folder as FolderPh,
  FolderOpen as FolderOpenPh,
  Funnel as FunnelPh,
  Gear as GearPh,
  Gift as GiftPh,
  GraduationCap as GraduationCapPh,
  GridFour as GridFourPh,
  Hash as HashPh,
  Heart as HeartPh,
  House as HousePh,
  Image as ImagePh,
  Info as InfoPh,
  Lightbulb as LightbulbPh,
  Lightning as LightningPh,
  Link as LinkPh,
  List as ListPh,
  ListChecks as ListChecksPh,
  Lock as LockPh,
  LockOpen as LockOpenPh,
  MagnifyingGlass as MagnifyingGlassPh,
  MagnifyingGlassMinus as MagnifyingGlassMinusPh,
  MagnifyingGlassPlus as MagnifyingGlassPlusPh,
  MapPin as MapPinPh,
  Microphone as MicrophonePh,
  MicrophoneSlash as MicrophoneSlashPh,
  Minus as MinusPh,
  Moon as MoonPh,
  NavigationArrow as NavigationArrowPh,
  PaintBrush as PaintBrushPh,
  Palette as PalettePh,
  PaperPlaneTilt as PaperPlaneTiltPh,
  Paperclip as PaperclipPh,
  Pause as PausePh,
  Pencil as PencilPh,
  PencilSimple as PencilSimplePh,
  Phone as PhonePh,
  Play as PlayPh,
  Plus as PlusPh,
  Power as PowerPh,
  Printer as PrinterPh,
  Quotes as QuotesPh,
  ShareNetwork as ShareNetworkPh,
  Shield as ShieldPh,
  SignIn as SignInPh,
  SignOut as SignOutPh,
  Smiley as SmileyPh,
  Sparkle as SparklePh,
  SpeakerHigh as SpeakerHighPh,
  SpeakerLow as SpeakerLowPh,
  SpeakerSlash as SpeakerSlashPh,
  Square as SquarePh,
  SquaresFour as SquaresFourPh,
  Stack as StackPh,
  Star as StarPh,
  Stop as StopPh,
  StopCircle as StopCirclePh,
  Sun as SunPh,
  SunHorizon as SunHorizonPh,
  Tag as TagPh,
  Target as TargetPh,
  TextT as TextTPh,
  Thermometer as ThermometerPh,
  ThumbsUp as ThumbsUpPh,
  ThumbsDown as ThumbsDownPh,
  Trash as TrashPh,
  Tree as TreePh,
  TrendDown as TrendDownPh,
  TrendUp as TrendUpPh,
  Upload as UploadPh,
  User as UserPh,
  UserMinus as UserMinusPh,
  UserPlus as UserPlusPh,
  Users as UsersPh,
  Warning as WarningPh,
  WarningCircle as WarningCirclePh,
  X as XPh,
  XCircle as XCirclePh,
  CreditCard as CreditCardPh,
  Globe as GlobePh,
  WifiHigh as WifiPh,
  WifiSlash as WifiSlashPh,
  Sliders as SlidersPh,
  Megaphone as MegaphonePh,
  Wallet as WalletPh,
  Wrench as WrenchPh,
  ClockCounterClockwise as HistoryPh,
  Ruler as RulerPh,
  BellSlash as BellOffPh,
  CheckSquare as CheckSquarePh,
  File as FilePh,
  FileImage as FileImagePh,
  FileArchive as FileArchivePh,
  MapTrifold as MapTrifoldPh,
  Medal as MedalPh,
} from '@phosphor-icons/react';

// Mapping des styles du thème vers les weights Phosphor
const STYLE_TO_WEIGHT = {
  'outline': 'regular',
  'outline-bold': 'bold', 
  'filled': 'fill',
  'duotone': 'duotone',
};

// Hook pour récupérer le style d'icône du localStorage
const getIconStyle = () => {
  try {
    const stored = localStorage.getItem('appCustomization');
    if (stored) {
      const config = JSON.parse(stored);
      return config?.icons?.style || 'outline';
    }
  } catch (e) {
    // Ignore errors
  }
  return 'outline';
};

// Hook personnalisé pour forcer le re-rendu quand le style change
const useIconStyle = () => {
  const [, forceUpdate] = useState(0);
  
  useEffect(() => {
    const handleStorageChange = () => {
      forceUpdate(n => n + 1);
    };
    
    // Écouter les changements de storage
    window.addEventListener('storage', handleStorageChange);
    // Écouter un événement personnalisé pour les changements internes
    window.addEventListener('customization-change', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('customization-change', handleStorageChange);
    };
  }, []);
  
  // Toujours lire la valeur actuelle du localStorage
  return getIconStyle();
};

// Créer un composant wrapper pour une icône Phosphor
const createThemedIcon = (PhosphorIcon, displayName) => {
  const ThemedIcon = React.forwardRef(({ weight: overrideWeight, ...props }, ref) => {
    const themeStyle = useIconStyle();
    const weight = overrideWeight || STYLE_TO_WEIGHT[themeStyle] || 'regular';
    return <PhosphorIcon ref={ref} weight={weight} {...props} />;
  });
  ThemedIcon.displayName = displayName;
  return ThemedIcon;
};

// Export des icônes avec support du thème - noms Lucide
export const Activity = createThemedIcon(ActivityPh, 'Activity');
export const ArrowLeft = createThemedIcon(ArrowLeftPh, 'ArrowLeft');
export const ArrowRight = createThemedIcon(ArrowRightPh, 'ArrowRight');
export const ArrowUp = createThemedIcon(ArrowUpPh, 'ArrowUp');
export const ArrowDown = createThemedIcon(ArrowDownPh, 'ArrowDown');
export const ArrowLeftRight = createThemedIcon(ArrowsLeftRightPh, 'ArrowLeftRight');
export const BarChart3 = createThemedIcon(ChartBarPh, 'BarChart3');
export const Bell = createThemedIcon(BellPh, 'Bell');
export const BookOpen = createThemedIcon(BookOpenPh, 'BookOpen');
export const Bookmark = createThemedIcon(BookmarkSimplePh, 'Bookmark');
export const Briefcase = createThemedIcon(BriefcasePh, 'Briefcase');
export const Building = createThemedIcon(BuildingsPh, 'Building');
export const Building2 = createThemedIcon(BuildingPh, 'Building2');
export const Calendar = createThemedIcon(CalendarPh, 'Calendar');
export const CalendarDays = createThemedIcon(CalendarBlankPh, 'CalendarDays');
export const CalendarRange = createThemedIcon(CalendarBlankPh, 'CalendarRange');
export const Camera = createThemedIcon(CameraPh, 'Camera');
export const Check = createThemedIcon(CheckPh, 'Check');
export const CheckCheck = createThemedIcon(ChecksPh, 'CheckCheck');
export const CheckCircle = createThemedIcon(CheckCirclePh, 'CheckCircle');
export const ChevronDown = createThemedIcon(CaretDownPh, 'ChevronDown');
export const ChevronLeft = createThemedIcon(CaretLeftPh, 'ChevronLeft');
export const ChevronRight = createThemedIcon(CaretRightPh, 'ChevronRight');
export const ChevronUp = createThemedIcon(CaretUpPh, 'ChevronUp');
export const Circle = createThemedIcon(CirclePh, 'Circle');
export const Clock = createThemedIcon(ClockPh, 'Clock');
export const Cloud = createThemedIcon(CloudPh, 'Cloud');
export const Compass = createThemedIcon(CompassPh, 'Compass');
export const Copy = createThemedIcon(CopyPh, 'Copy');
export const Crosshair = createThemedIcon(CrosshairPh, 'Crosshair');
export const Download = createThemedIcon(DownloadPh, 'Download');
export const Edit3 = createThemedIcon(PencilSimplePh, 'Edit3');
export const ExternalLink = createThemedIcon(ArrowSquareOutPh, 'ExternalLink');
export const Eye = createThemedIcon(EyePh, 'Eye');
export const EyeOff = createThemedIcon(EyeSlashPh, 'EyeOff');
export const FileDown = createThemedIcon(FileArrowDownPh, 'FileDown');
export const FileOutput = createThemedIcon(ExportPh, 'FileOutput');
export const Files = createThemedIcon(FilesPh, 'Files');
export const FileSpreadsheet = createThemedIcon(FileXlsPh, 'FileSpreadsheet');
export const FileText = createThemedIcon(FileTextPh, 'FileText');
export const FileAudio = createThemedIcon(FileAudioPh, 'FileAudio');
export const Filter = createThemedIcon(FunnelPh, 'Filter');
export const Flame = createThemedIcon(FlamePh, 'Flame');
export const Folder = createThemedIcon(FolderPh, 'Folder');
export const FolderOpen = createThemedIcon(FolderOpenPh, 'FolderOpen');
export const GraduationCap = createThemedIcon(GraduationCapPh, 'GraduationCap');
export const Gift = createThemedIcon(GiftPh, 'Gift');
export const GripVertical = createThemedIcon(DotsSixVerticalPh, 'GripVertical');
export const Hash = createThemedIcon(HashPh, 'Hash');
export const Heart = createThemedIcon(HeartPh, 'Heart');
export const Home = createThemedIcon(HousePh, 'Home');
export const HardDrive = createThemedIcon(HardDrivesPh, 'HardDrive');
export const Image = createThemedIcon(ImagePh, 'Image');
export const Info = createThemedIcon(InfoPh, 'Info');
export const Layers = createThemedIcon(StackPh, 'Layers');
export const LayoutDashboard = createThemedIcon(SquaresFourPh, 'LayoutDashboard');
export const LayoutGrid = createThemedIcon(GridFourPh, 'LayoutGrid');
export const Lightbulb = createThemedIcon(LightbulbPh, 'Lightbulb');
export const Link2 = createThemedIcon(LinkPh, 'Link2');
export const List = createThemedIcon(ListPh, 'List');
export const ListTodo = createThemedIcon(ListChecksPh, 'ListTodo');
export const Loader2 = createThemedIcon(CircleNotchPh, 'Loader2');
export const Lock = createThemedIcon(LockPh, 'Lock');
export const LogIn = createThemedIcon(SignInPh, 'LogIn');
export const LogOut = createThemedIcon(SignOutPh, 'LogOut');
export const Mail = createThemedIcon(EnvelopePh, 'Mail');
export const MapPin = createThemedIcon(MapPinPh, 'MapPin');
export const MapPinIcon = createThemedIcon(MapPinPh, 'MapPinIcon');
export const Maximize = createThemedIcon(ArrowsOutCardinalPh, 'Maximize');
export const Maximize2 = createThemedIcon(ArrowsOutPh, 'Maximize2');
export const Menu = createThemedIcon(ListPh, 'Menu');
export const MessageCircle = createThemedIcon(ChatCirclePh, 'MessageCircle');
export const MessageSquare = createThemedIcon(ChatSquarePh, 'MessageSquare');
export const Mic = createThemedIcon(MicrophonePh, 'Mic');
export const MicOff = createThemedIcon(MicrophoneSlashPh, 'MicOff');
export const Minimize2 = createThemedIcon(ArrowsInPh, 'Minimize2');
export const Minus = createThemedIcon(MinusPh, 'Minus');
export const Moon = createThemedIcon(MoonPh, 'Moon');
export const MoreHorizontal = createThemedIcon(DotsThreePh, 'MoreHorizontal');
export const MoreVertical = createThemedIcon(DotsThreeVerticalPh, 'MoreVertical');
export const Navigation = createThemedIcon(NavigationArrowPh, 'Navigation');
export const Paintbrush = createThemedIcon(PaintBrushPh, 'Paintbrush');
export const Palette = createThemedIcon(PalettePh, 'Palette');
export const Palmtree = createThemedIcon(TreePh, 'Palmtree');
export const Paperclip = createThemedIcon(PaperclipPh, 'Paperclip');
export const Pause = createThemedIcon(PausePh, 'Pause');
export const Pencil = createThemedIcon(PencilPh, 'Pencil');
export const Phone = createThemedIcon(PhonePh, 'Phone');
export const Play = createThemedIcon(PlayPh, 'Play');
export const Plus = createThemedIcon(PlusPh, 'Plus');
export const Power = createThemedIcon(PowerPh, 'Power');
export const Printer = createThemedIcon(PrinterPh, 'Printer');
export const Quote = createThemedIcon(QuotesPh, 'Quote');
export const RefreshCw = createThemedIcon(ArrowClockwisePh, 'RefreshCw');
export const RotateCcw = createThemedIcon(ArrowCounterClockwisePh, 'RotateCcw');
export const RotateCw = createThemedIcon(ArrowClockwisePh, 'RotateCw');
export const Save = createThemedIcon(FloppyDiskPh, 'Save');
export const Scaling = createThemedIcon(ArrowsOutSimplePh, 'Scaling');
export const Search = createThemedIcon(MagnifyingGlassPh, 'Search');
export const Map = createThemedIcon(MapTrifoldPh, 'Map');
export const Send = createThemedIcon(PaperPlaneTiltPh, 'Send');
export const Settings = createThemedIcon(GearPh, 'Settings');
export const Share2 = createThemedIcon(ShareNetworkPh, 'Share2');
export const Shield = createThemedIcon(ShieldPh, 'Shield');
export const Smartphone = createThemedIcon(DeviceMobilePh, 'Smartphone');
export const Smile = createThemedIcon(SmileyPh, 'Smile');
export const Sparkles = createThemedIcon(SparklePh, 'Sparkles');
export const Square = createThemedIcon(SquarePh, 'Square');
export const Star = createThemedIcon(StarPh, 'Star');
export const Stop = createThemedIcon(StopPh, 'Stop');
export const StopCircle = createThemedIcon(StopCirclePh, 'StopCircle');
export const Sun = createThemedIcon(SunPh, 'Sun');
export const Sunrise = createThemedIcon(SunHorizonPh, 'Sunrise');
export const Tag = createThemedIcon(TagPh, 'Tag');
export const Target = createThemedIcon(TargetPh, 'Target');
export const Thermometer = createThemedIcon(ThermometerPh, 'Thermometer');
export const ThumbsUp = createThemedIcon(ThumbsUpPh, 'ThumbsUp');
export const ThumbsDown = createThemedIcon(ThumbsDownPh, 'ThumbsDown');
export const Trash2 = createThemedIcon(TrashPh, 'Trash2');
export const TrendingDown = createThemedIcon(TrendDownPh, 'TrendingDown');
export const TrendingUp = createThemedIcon(TrendUpPh, 'TrendingUp');
export const Type = createThemedIcon(TextTPh, 'Type');
export const Unlock = createThemedIcon(LockOpenPh, 'Unlock');
export const Upload = createThemedIcon(UploadPh, 'Upload');
export const User = createThemedIcon(UserPh, 'User');
export const UserMinus = createThemedIcon(UserMinusPh, 'UserMinus');
export const UserPlus = createThemedIcon(UserPlusPh, 'UserPlus');
export const Users = createThemedIcon(UsersPh, 'Users');
export const Volume1 = createThemedIcon(SpeakerLowPh, 'Volume1');
export const Volume2 = createThemedIcon(SpeakerHighPh, 'Volume2');
export const VolumeX = createThemedIcon(SpeakerSlashPh, 'VolumeX');
export const AlertTriangle = createThemedIcon(WarningPh, 'AlertTriangle');
export const AlertCircle = createThemedIcon(WarningCirclePh, 'AlertCircle');
export const X = createThemedIcon(XPh, 'X');
export const XCircle = createThemedIcon(XCirclePh, 'XCircle');
export const Zap = createThemedIcon(LightningPh, 'Zap');
export const ZoomIn = createThemedIcon(MagnifyingGlassPlusPh, 'ZoomIn');
export const ZoomOut = createThemedIcon(MagnifyingGlassMinusPh, 'ZoomOut');
export const ChevronsLeft = createThemedIcon(CaretDoubleLeftPh, 'ChevronsLeft');
export const ChevronsRight = createThemedIcon(CaretDoubleRightPh, 'ChevronsRight');
export const AtSign = createThemedIcon(AtPh, 'AtSign');
export const CreditCard = createThemedIcon(CreditCardPh, 'CreditCard');
export const Globe = createThemedIcon(GlobePh, 'Globe');
export const Wifi = createThemedIcon(WifiPh, 'Wifi');
export const WifiOff = createThemedIcon(WifiSlashPh, 'WifiOff');
export const Settings2 = createThemedIcon(SlidersPh, 'Settings2');
export const Megaphone = createThemedIcon(MegaphonePh, 'Megaphone');
export const Wallet = createThemedIcon(WalletPh, 'Wallet');
export const Wrench = createThemedIcon(WrenchPh, 'Wrench');
export const History = createThemedIcon(HistoryPh, 'History');
export const Ruler = createThemedIcon(RulerPh, 'Ruler');
export const BellOff = createThemedIcon(BellOffPh, 'BellOff');
export const CheckSquare = createThemedIcon(CheckSquarePh, 'CheckSquare');
export const File = createThemedIcon(FilePh, 'File');
export const FileImage = createThemedIcon(FileImagePh, 'FileImage');
export const FileArchive = createThemedIcon(FileArchivePh, 'FileArchive');
export const Award = createThemedIcon(MedalPh, 'Award');

// Exports directs des icônes Phosphor originales pour usage avancé
export const ActivityIcon = ActivityPh;
export const ArrowLeftIcon = ArrowLeftPh;
export const BellIcon = BellPh;
export const CalendarIcon = CalendarPh;
export const CameraIcon = CameraPh;
export const CheckIcon = CheckPh;
export const ClockIcon = ClockPh;
export const GearIcon = GearPh;
export const HouseIcon = HousePh;
export const UserIcon = UserPh;
export const UsersIcon = UsersPh;
export const XIcon = XPh;

// Aliases supplémentaires migrés depuis lucide-react (Phase EE 02/06/2026)
export const Bot = createThemedIcon(RobotPh, 'Bot');
export const QrCode = createThemedIcon(QrCodePh, 'QrCode');
